import { Star, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { propFirmData } from '../data/propFirmDataHomepage';
import { propFirmLogos } from '../data/propFirmLogos';

interface PropFirmRating {
  id: string;
  name: string;
  rating: number;
  reviewCount: number;
  reviews: {
    author: string;
    rating: number;
    comment: string;
    date: string;
  }[];
}

const propFirmRatings: PropFirmRating[] = [
  {
    id: 'cti',
    name: propFirmData.cti.name,
    rating: propFirmData.cti.rating,
    reviewCount: propFirmData.cti.reviewCount,
    reviews: [
      {
        author: 'Michael S.',
        rating: 5,
        comment: 'Excellent support team and very clear rules. Passed both phases and now trading funded account.',
        date: 'March 15, 2024'
      },
      {
        author: 'Emma L.',
        rating: 4,
        comment: 'Good platform but the drawdown rules are a bit strict. Otherwise great experience.',
        date: 'March 12, 2024'
      },
      {
        author: 'David R.',
        rating: 5,
        comment: 'Best prop firm I\'ve used. Educational resources are top-notch!',
        date: 'March 10, 2024'
      }
    ]
  },
  {
    id: 'ttp',
    name: propFirmData.ttp.name,
    rating: propFirmData.ttp.rating,
    reviewCount: propFirmData.ttp.reviewCount,
    reviews: [
      {
        author: 'Sarah K.',
        rating: 5,
        comment: 'The 80% profit split is great. Fast withdrawals and excellent customer service.',
        date: 'March 14, 2024'
      },
      {
        author: 'James M.',
        rating: 5,
        comment: 'Passed the challenge in 2 weeks. Very transparent and professional.',
        date: 'March 11, 2024'
      },
      {
        author: 'Anna P.',
        rating: 4,
        comment: 'Excellent trading conditions. The scaling plan is motivating.',
        date: 'March 8, 2024'
      }
    ]
  },
  {
    id: 'fxify',
    name: propFirmData.fxify.name,
    rating: propFirmData.fxify.rating,
    reviewCount: propFirmData.fxify.reviewCount,
    reviews: [
      {
        author: 'Thomas H.',
        rating: 5,
        comment: '24/7 support is fantastic. They really care about their traders.',
        date: 'March 13, 2024'
      },
      {
        author: 'Lisa R.',
        rating: 4,
        comment: 'Flexible trading conditions and good platform stability.',
        date: 'March 9, 2024'
      },
      {
        author: 'Mark D.',
        rating: 4,
        comment: 'Weekly payouts are processed quickly. Good communication.',
        date: 'March 7, 2024'
      }
    ]
  },
  {
    id: 'alpha-capital',
    name: propFirmData['alpha-capital'].name,
    rating: propFirmData['alpha-capital'].rating,
    reviewCount: propFirmData['alpha-capital'].reviewCount,
    reviews: [
      {
        author: 'Peter W.',
        rating: 4,
        comment: 'Great value for money. The evaluation fee is very reasonable.',
        date: 'March 12, 2024'
      },
      {
        author: 'Sophie L.',
        rating: 5,
        comment: 'Regular webinars have helped improve my trading significantly.',
        date: 'March 8, 2024'
      },
      {
        author: 'Ryan M.',
        rating: 4,
        comment: 'Solid platform with good risk management tools.',
        date: 'March 6, 2024'
      }
    ]
  },
  {
    id: 'e8',
    name: propFirmData.e8.name,
    rating: propFirmData.e8.rating,
    reviewCount: propFirmData.e8.reviewCount,
    reviews: [
      {
        author: 'Chris B.',
        rating: 4,
        comment: 'Modern interface and great trading conditions.',
        date: 'March 11, 2024'
      },
      {
        author: 'Maria S.',
        rating: 5,
        comment: 'The hybrid evaluation system is innovative and fair.',
        date: 'March 7, 2024'
      },
      {
        author: 'Daniel K.',
        rating: 4,
        comment: 'Good platform stability and responsive support team.',
        date: 'March 5, 2024'
      }
    ]
  }
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-5 h-5 ${
            i < Math.floor(rating) 
              ? 'text-yellow-400 fill-current' 
              : 'text-gray-300'
          }`}
        />
      ))}
      <span className="ml-2 font-medium">{rating.toFixed(1)}</span>
    </div>
  );
}

function ReviewCard({ review }: { review: PropFirmRating['reviews'][0] }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-medium">
              {review.author.charAt(0)}
            </span>
          </div>
          <div>
            <div className="font-medium">{review.author}</div>
            <div className="text-sm text-gray-500">{review.date}</div>
          </div>
        </div>
        <StarRating rating={review.rating} />
      </div>
      <p className="text-gray-600">{review.comment}</p>
    </div>
  );
}

export function TestimonialsPage() {
  // Sort firms by rating
  const sortedFirms = [...propFirmRatings].sort((a, b) => b.rating - a.rating);

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Prop Trading Firm Reviews & Testimonials",
    "description": "Read authentic trader reviews and testimonials for the top prop trading firms. Compare ratings and make an informed decision.",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": sortedFirms.map((firm, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Review",
          "itemReviewed": {
            "@type": "Organization",
            "name": firm.name
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": firm.rating,
            "bestRating": "5"
          },
          "reviewCount": firm.reviewCount
        }
      }))
    }
  };

  return (
    <>
      <Helmet>
        <title>Prop Trading Firm Reviews & Testimonials | PropViper</title>
        <meta 
          name="description" 
          content="Read authentic trader reviews and testimonials for the top prop trading firms. Compare ratings and make an informed decision."
        />
        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-4">
              Trader Reviews & Testimonials
            </h1>
            <p className="text-xl text-gray-600 text-center mb-12">
              Read authentic reviews from verified traders about their experiences with top prop trading firms.
            </p>

            {/* Ratings Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-12">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left">Firm</th>
                    <th className="px-6 py-4 text-left">Rating</th>
                    <th className="px-6 py-4 text-left">Reviews</th>
                    <th className="px-6 py-4 text-left">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedFirms.map((firm) => (
                    <tr key={firm.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 p-1.5">
                            <img 
                              src={propFirmLogos[firm.id as keyof typeof propFirmLogos]?.logo} 
                              alt={firm.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span className="font-medium">{firm.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <StarRating rating={firm.rating} />
                      </td>
                      <td className="px-6 py-4">{firm.reviewCount}</td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/reviews/${firm.id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center"
                        >
                          Read Review <ArrowRight className="ml-1 w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Reviews by Firm */}
            {sortedFirms.map((firm) => (
              <div key={firm.id} className="mb-12">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 p-1.5">
                      <img 
                        src={propFirmLogos[firm.id as keyof typeof propFirmLogos]?.logo} 
                        alt={firm.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <h2 className="text-2xl font-bold">{firm.name}</h2>
                  </div>
                  <Link
                    to={`/reviews/${firm.id}`}
                    className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center"
                  >
                    View Full Review <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
                <div className="grid gap-6">
                  {firm.reviews.map((review, index) => (
                    <ReviewCard key={index} review={review} />
                  ))}
                </div>
              </div>
            ))}

            {/* CTA Section */}
            <div className="bg-blue-600 rounded-2xl shadow-lg p-8 text-center text-white">
              <h2 className="text-2xl font-bold mb-3">Ready to Start Your Trading Career?</h2>
              <p className="text-blue-100 mb-6">
                Compare the top prop firms and choose the best one for your trading style.
              </p>
              <Link
                to="/compare"
                className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Compare Top Firms <ArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}